import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Work as WorkIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useFormik } from 'formik';
import * as yup from 'yup';

// Use env var if set, otherwise use relative URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let base = process.env.REACT_APP_API_URL;
    base = base.endsWith('/') ? base.slice(0, -1) : base;
    if (base.endsWith('/api')) {
      base = base.slice(0, -4);
    }
    return base;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '';
  }
  return 'http://localhost:3000';
};

const qualifications = [
  { value: 'SSC', label: 'SSC / 10th' },
  { value: 'Intermediate', label: 'Intermediate / 12th' },
  { value: 'ITI', label: 'ITI / Diploma' },
  { value: 'Degree', label: 'Degree' },
  { value: 'B.Tech', label: 'B.Tech' },
  { value: 'M.Tech', label: 'M.Tech' },
  { value: 'Nursing', label: 'Nursing' },
];

const validationSchema = yup.object({
  userName: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  villageCity: yup.string().required('Village/City is required'),
  address: yup.string().required('Address is required'),
  qualification: yup.string().required('Qualification is required'),
  otherInfo: yup.string(),
});

const JobAlertsRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Check if user is already logged in
    if (user?.phone) {
      setIsAuthenticated(true);
      setPhoneNumber(user.phone.replace(/\D/g, '').slice(-10));
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const phoneDigits = phoneNumber.replace(/\D/g, '');
      const lastFourDigits = phoneDigits.slice(-4);

      if (password !== lastFourDigits) {
        setError('Invalid password. Please use last 4 digits of your phone number.');
        setLoading(false);
        return;
      }

      // Normalize phone
      const normalizedPhone = phoneDigits.length === 10 ? `+91${phoneDigits}` : phoneDigits;

      const result = await login(normalizedPhone, password, null, 'user');
      if (result.success) {
        setIsAuthenticated(true);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      userName: user?.full_name || '',
      villageCity: '',
      address: '',
      qualification: '',
      otherInfo: '',
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setSuccess(false);

      try {
        const phoneDigits = (user?.phone || phoneNumber).replace(/\D/g, '');
        const lastFourDigits = phoneDigits.slice(-4);
        const normalizedPhone = phoneDigits.length === 10 ? `+91${phoneDigits}` : phoneDigits;

        const baseUrl = getApiBaseUrl();
        const response = await axios.post(
          `${baseUrl}/api/jobalerts/enquiries/public`,
          {
            userPhone: normalizedPhone,
            password: lastFourDigits,
            userName: values.userName,
            villageCity: values.villageCity,
            address: values.address,
            qualification: values.qualification,
            otherInfo: values.otherInfo || null,
          }
        );

        if (response.data.success) {
          setSuccess(true);
          setTimeout(() => {
            navigate('/user/dashboard');
          }, 2000);
        } else {
          setError(response.data.message || 'Failed to submit enquiry');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to submit enquiry. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  // Update form when user is authenticated
  useEffect(() => {
    if (user?.phone && user?.full_name) {
      formik.setFieldValue('userName', user.full_name);
      setPhoneNumber(user.phone.replace(/\D/g, '').slice(-10));
    }
  }, [user]);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <WorkIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Job Alerts Registration
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please login to register for job alerts
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10 digit phone number"
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <TextField
                fullWidth
                label="Password (Last 4 digits)"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="Last 4 digits of phone number"
                required
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 4 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || phoneNumber.length !== 10 || password.length !== 4}
                sx={{ py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Login & Continue'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <WorkIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Job Alerts Registration Form
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Fill in your details to register for job alerts
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Registration successful! Redirecting to dashboard...
            </Alert>
          )}

          <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="userName"
                  name="userName"
                  label="Full Name *"
                  value={formik.values.userName}
                  onChange={formik.handleChange}
                  error={formik.touched.userName && Boolean(formik.errors.userName)}
                  helperText={formik.touched.userName && formik.errors.userName}
                  required
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="mobileNumber"
                  name="mobileNumber"
                  label="Mobile Number"
                  value={phoneNumber}
                  disabled
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="villageCity"
                  name="villageCity"
                  label="Village / City *"
                  value={formik.values.villageCity}
                  onChange={formik.handleChange}
                  error={formik.touched.villageCity && Boolean(formik.errors.villageCity)}
                  helperText={formik.touched.villageCity && formik.errors.villageCity}
                  required
                  InputProps={{
                    startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="address"
                  name="address"
                  label="Address *"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  required
                  multiline
                  rows={3}
                  InputProps={{
                    startAdornment: <HomeIcon sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="qualification"
                  name="qualification"
                  label="Qualification (Job Alert Category) *"
                  value={formik.values.qualification}
                  onChange={formik.handleChange}
                  error={formik.touched.qualification && Boolean(formik.errors.qualification)}
                  helperText={formik.touched.qualification && formik.errors.qualification}
                  required
                  select
                  InputProps={{
                    startAdornment: <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                >
                  {qualifications.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="otherInfo"
                  name="otherInfo"
                  label="Other Useful Information"
                  value={formik.values.otherInfo}
                  onChange={formik.handleChange}
                  multiline
                  rows={3}
                  placeholder="Any additional information you'd like to provide..."
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/user/dashboard')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ minWidth: 150 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Submit Registration'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default JobAlertsRegistration;






